<script setup lang="ts">
import { promotionalCodesApi } from '@/api/promotional-codes';
import { debounce } from 'lodash';
import { computed, ref, watch } from 'vue';

interface Props {
  productId: number
}

const props = defineProps<Props>();

const promotionalCode = ref();

const foundCode = ref<PromotionalCode|null>();

const loading = ref(false);
const error = ref(false);
const showCodeBenefit = ref(false);

const emit = defineEmits<{
  (e: 'update:value', value: number | null): void
}>();

function findPromotionalCode() {
  showCodeBenefit.value = true;
  loading.value = true;
  error.value = false;
  promotionalCodesApi.search(promotionalCode.value, props.productId)
    .then((response) => {
      if (response.data?.promotionalCode) {
        foundCode.value = response.data.promotionalCode as PromotionalCode;
        emit('update:value', foundCode.value.id);
      } else {
        error.value = true;
        foundCode.value = null;
        emit('update:value', null);
      }
      loading.value = false;
    });
}

watch(promotionalCode, () => {
  if (promotionalCode.value === '') {
    error.value = false;
    emit('update:value', null);
  } else {
    loading.value = true;
    // eslint-disable-next-line no-magic-numbers
    debounce(findPromotionalCode, 500)();
  }
});

const canRedeem = computed(() => foundCode.value &&
  (foundCode.value?.maxRedeems === 0 || foundCode.value?.redeemsCount < foundCode.value?.maxRedeems));

const showCampaignDetails = ref(false);
function toggleCampaignDetails() {
  showCampaignDetails.value = !showCampaignDetails.value;
}
</script>
<template>
  <div class="flex flex-col space-y-4">
    <base-input
      id="refcode"
      v-model="promotionalCode"
      name="refcode"
      label="Código promocional (opcional)"
      placeholder="Código promocional (opcional)"
      :error="error"
    />
    <div
      v-if="showCodeBenefit && promotionalCode"
      class="flex w-full flex-col items-center rounded-lg bg-z-gray-50 p-4 text-center text-sm"
    >
      <img
        v-if="loading"
        src="@/assets/icons/loading.svg"
        class="w-10 animate-spin-slow"
      >
      <template v-else-if="error">
        El código ingresado no es válido
      </template>
      <template
        v-else-if="foundCode && canRedeem"
      >
        <img
          src="@/assets/images/texas-ribs.png"
          class="w-40"
        >
        <p class="mt-2 text-base">
          Obtén de regalo una botella de vino Castillo de Molina en Texas Ribs Patio Bellavista.
        </p>
        <button
          class="mt-1 flex py-2 text-sm text-z-gray-700 underline"
          @click="toggleCampaignDetails"
        >
          <p>Ver detalles</p>
          <img
            src="@/assets/icons/chevron-down.svg"
            class="ml-1 w-4"
          >
        </button>
        <div
          v-if="showCampaignDetails"
          class="text-xs"
        >
          <p class="text-left text-z-gray-900">
            Al completar el pago de tu arriendo, recibirás un código de beneficio que podrás canjear
            luego de pagar tu cuenta en Texas Ribs Patio Bellavista.
            (Ver <a
              href="/TERMINOS_Y_CONDICIONES_CAMPAÑA_ZIRKULAR_PICA_DEL_SKI_Y_TEXAS_RIBS.pdf"
              rel="noopener"
              target="_blank"
              class="text-z-gray-700 underline"
            >Términos y condiciones</a>)
          </p>
        </div>
      </template>
      <template
        v-else-if="!canRedeem"
      >
        El código ya ha sido canjeado.
      </template>
    </div>
  </div>
</template>
